import { 
  collection, 
  getDocs, 
  addDoc, 
  deleteDoc, 
  doc, 
  writeBatch,
  query,
  orderBy,
  limit,
  where,
  getDoc
} from 'firebase/firestore';
import { db } from '../firebase';

export class BackupRestore {
  static async createBackup(userId, userEmail, backupName = null, maxBackups = null) {
    try {
      console.log('Starting database backup...');
      console.log('User ID:', userId);
      console.log('User Email:', userEmail);
      console.log('Backup Name:', backupName);
      
      // Get all collections data
      const collections = ['players', 'tournaments', 'matches', 'scores', 'golfCourses', 'users', 'transactionLogs'];
      const backupData = {};
      
      for (const collectionName of collections) {
        console.log(`Fetching data from collection: ${collectionName}`);
        const snapshot = await getDocs(collection(db, collectionName));
        backupData[collectionName] = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        console.log(`Found ${backupData[collectionName].length} records in ${collectionName}`);
      }
      
      // Create backup metadata
      const backup = {
        name: backupName || `Backup_${new Date().toISOString().split('T')[0]}`,
        timestamp: new Date(),
        userId,
        userEmail,
        collections: Object.keys(backupData),
        recordCounts: Object.keys(backupData).reduce((acc, key) => {
          acc[key] = backupData[key].length;
          return acc;
        }, {}),
        data: backupData
      };
      
      // Save backup to database
      console.log('Attempting to save backup to database...');
      console.log('Backup data structure:', Object.keys(backup));
      console.log('Record counts:', backup.recordCounts);
      
      const backupRef = await addDoc(collection(db, 'backups'), backup);
      
      console.log('Backup created successfully:', backupRef.id);
      
      // Auto-cleanup old backups if maxBackups is set
      if (maxBackups && maxBackups > 0) {
        await this.cleanupOldBackups(maxBackups);
      }
      
      return { id: backupRef.id, ...backup };
    } catch (error) {
      console.error('Error creating backup:', error);
      throw error;
    }
  }

  static async getBackups() {
    try {
      const q = query(
        collection(db, 'backups'),
        orderBy('timestamp', 'desc'),
        limit(100)
      );
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('Error fetching backups:', error);
      return [];
    }
  }

  static async deleteBackup(backupId) {
    try {
      console.log('Deleting backup:', backupId);
      await deleteDoc(doc(db, 'backups', backupId));
      console.log('Backup deleted successfully');
      return true;
    } catch (error) {
      console.error('Error deleting backup:', error);
      throw error;
    }
  }

  static async deleteRestorePoint(restorePointId) {
    try {
      console.log('Deleting restore point:', restorePointId);
      await deleteDoc(doc(db, 'restorePoints', restorePointId));
      console.log('Restore point deleted successfully');
      return true;
    } catch (error) {
      console.error('Error deleting restore point:', error);
      throw error;
    }
  }

  static async cleanupOldBackups(maxBackups) {
    try {
      console.log(`Cleaning up old backups, keeping only ${maxBackups} most recent...`);
      
      const allBackups = await this.getBackups();
      
      // Keep only the most recent backups
      if (allBackups.length > maxBackups) {
        const backupsToDelete = allBackups.slice(maxBackups);
        
        for (const backup of backupsToDelete) {
          await this.deleteBackup(backup.id);
          console.log(`Deleted old backup: ${backup.name}`);
        }
        
        console.log(`Cleanup complete. Deleted ${backupsToDelete.length} old backups.`);
        return backupsToDelete.length;
      }
      
      return 0;
    } catch (error) {
      console.error('Error cleaning up old backups:', error);
      throw error;
    }
  }

  static async restoreFromBackup(backupId, userId, userEmail) {
    try {
      console.log('Starting restore from backup:', backupId);
      
      // Get backup data
      const backupDocRef = doc(db, 'backups', backupId);
      const backupDoc = await getDoc(backupDocRef);
      if (!backupDoc.exists()) {
        throw new Error('Backup not found');
      }
      
      const backup = backupDoc.data();
      
      // Clear existing data (be careful in production!)
      const collections = ['players', 'tournaments', 'matches', 'scores', 'golfCourses'];
      const batch = writeBatch(db);
      
      for (const collectionName of collections) {
        const snapshot = await getDocs(collection(db, collectionName));
        snapshot.docs.forEach(docSnapshot => {
          batch.delete(docSnapshot.ref);
        });
      }
      
      await batch.commit();
      
      // Restore data
      const restoreBatch = writeBatch(db);
      
      for (const collectionName of Object.keys(backup.data)) {
        if (collectionName !== 'users' && collectionName !== 'transactionLogs') {
          for (const record of backup.data[collectionName]) {
            const newDocRef = doc(collection(db, collectionName));
            restoreBatch.set(newDocRef, {
              ...record,
              restoredAt: new Date(),
              restoredBy: userId
            });
          }
        }
      }
      
      await restoreBatch.commit();
      
      console.log('Restore completed successfully');
      return true;
    } catch (error) {
      console.error('Error restoring backup:', error);
      throw error;
    }
  }

  static async createRestorePoint(name, userId, userEmail) {
    try {
      const restorePoint = {
        name,
        timestamp: new Date(),
        userId,
        userEmail,
        type: 'RESTORE_POINT'
      };
      
      const restorePointRef = await addDoc(collection(db, 'restorePoints'), restorePoint);
      
      // Create a backup for this restore point
      const backup = await this.createBackup(userId, userEmail, `RestorePoint_${name}`);
      
      return {
        id: restorePointRef.id,
        backupId: backup.id,
        ...restorePoint
      };
    } catch (error) {
      console.error('Error creating restore point:', error);
      throw error;
    }
  }

  static async getRestorePoints() {
    try {
      const q = query(
        collection(db, 'restorePoints'),
        orderBy('timestamp', 'desc'),
        limit(20)
      );
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('Error fetching restore points:', error);
      return [];
    }
  }

  static async restoreToPoint(restorePointId, userId, userEmail) {
    try {
      // Get restore point
      const restorePointDocRef = doc(db, 'restorePoints', restorePointId);
      const restorePointDoc = await getDoc(restorePointDocRef);
      if (!restorePointDoc.exists()) {
        throw new Error('Restore point not found');
      }
      
      const restorePoint = restorePointDoc.data();
      
      // Find the associated backup
      const backups = await this.getBackups();
      const associatedBackup = backups.find(backup => 
        backup.name === `RestorePoint_${restorePoint.name}` && 
        new Date(backup.timestamp) <= new Date(restorePoint.timestamp)
      );
      
      if (!associatedBackup) {
        throw new Error('Associated backup not found');
      }
      
      // Restore from the backup
      return await this.restoreFromBackup(associatedBackup.id, userId, userEmail);
    } catch (error) {
      console.error('Error restoring to point:', error);
      throw error;
    }
  }

  // Download backup as JSON file
  static async downloadBackup(backupId) {
    try {
      console.log('Downloading backup:', backupId);
      
      // Get backup data
      const backupDocRef = doc(db, 'backups', backupId);
      const backupDoc = await getDoc(backupDocRef);
      if (!backupDoc.exists()) {
        throw new Error('Backup not found');
      }
      
      const backup = backupDoc.data();
      
      // Prepare backup for download
      const downloadData = {
        id: backupId,
        name: backup.name,
        timestamp: backup.timestamp,
        collections: backup.collections,
        recordCounts: backup.recordCounts,
        data: backup.data,
        exportedAt: new Date().toISOString(),
        version: '1.0'
      };
      
      // Create blob and download
      const dataStr = JSON.stringify(downloadData, null, 2);
      const dataBlob = new Blob([dataStr], { type: 'application/json' });
      const url = URL.createObjectURL(dataBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${backup.name}_${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      console.log('Backup downloaded successfully');
      return true;
    } catch (error) {
      console.error('Error downloading backup:', error);
      throw error;
    }
  }

  // Restore from uploaded JSON file
  static async restoreFromFile(fileData, userId, userEmail) {
    try {
      console.log('Starting restore from uploaded file...');
      
      // Validate file data
      if (!fileData.data || !fileData.collections) {
        throw new Error('Invalid backup file format');
      }
      
      console.log('File validation passed');
      console.log('Collections in file:', fileData.collections);
      console.log('Record counts:', fileData.recordCounts);
      
      // Clear existing data
      const collections = ['players', 'tournaments', 'matches', 'scores', 'golfCourses'];
      const batch = writeBatch(db);
      
      for (const collectionName of collections) {
        console.log(`Clearing collection: ${collectionName}`);
        const snapshot = await getDocs(collection(db, collectionName));
        snapshot.docs.forEach(docSnapshot => {
          batch.delete(docSnapshot.ref);
        });
      }
      
      await batch.commit();
      console.log('Existing data cleared');
      
      // Restore data from file
      const restoreBatch = writeBatch(db);
      let restoredCount = 0;
      
      for (const collectionName of Object.keys(fileData.data)) {
        if (collectionName !== 'users' && collectionName !== 'transactionLogs') {
          console.log(`Restoring collection: ${collectionName}`);
          for (const record of fileData.data[collectionName]) {
            const { id, ...recordData } = record; // Remove id from data
            const newDocRef = doc(collection(db, collectionName));
            restoreBatch.set(newDocRef, {
              ...recordData,
              restoredAt: new Date(),
              restoredBy: userId,
              restoredFrom: 'file'
            });
            restoredCount++;
          }
        }
      }
      
      await restoreBatch.commit();
      
      console.log(`Restore completed successfully. Restored ${restoredCount} records.`);
      return { success: true, restoredCount };
    } catch (error) {
      console.error('Error restoring from file:', error);
      throw error;
    }
  }

  // Create and download a fresh backup
  static async createAndDownloadBackup(userId, userEmail, backupName = null) {
    try {
      console.log('Creating backup for download...');
      
      // Get all collections data
      const collections = ['players', 'tournaments', 'matches', 'scores', 'golfCourses', 'users', 'transactionLogs'];
      const backupData = {};
      
      for (const collectionName of collections) {
        console.log(`Fetching data from collection: ${collectionName}`);
        const snapshot = await getDocs(collection(db, collectionName));
        backupData[collectionName] = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        console.log(`Found ${backupData[collectionName].length} records in ${collectionName}`);
      }
      
      // Create backup metadata
      const backup = {
        name: backupName || `Backup_${new Date().toISOString().split('T')[0]}`,
        timestamp: new Date(),
        userId,
        userEmail,
        collections: Object.keys(backupData),
        recordCounts: Object.keys(backupData).reduce((acc, key) => {
          acc[key] = backupData[key].length;
          return acc;
        }, {}),
        data: backupData,
        exportedAt: new Date().toISOString(),
        version: '1.0'
      };
      
      // Create blob and download
      const dataStr = JSON.stringify(backup, null, 2);
      const dataBlob = new Blob([dataStr], { type: 'application/json' });
      const url = URL.createObjectURL(dataBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${backup.name}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      console.log('Backup downloaded successfully');
      return { success: true, backup };
    } catch (error) {
      console.error('Error creating and downloading backup:', error);
      throw error;
    }
  }
}

export default BackupRestore;
